import React from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { IconButton, Typography } from '@material-ui/core';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import CloseIcon from '@material-ui/icons/Close';
import { SkeletonLoader } from '../../components/loading/SkeletonLoader';

// The look the Enquiry context-menu dialogs share with the email template dialog and the Follow Up / Send Email dialogs:
// header with a close button, labelled grey card for the fields, a ruled footer. Only presentation lives here - every
// dialog keeps its own form, validation and saving.
export const useDialogPartsStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialogHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(2),
            padding: theme.spacing(2, 2.5),
            borderBottom: `1px solid ${theme.palette.divider}`,
            flexShrink: 0,
        },
        titleText: {
            fontSize: 15,
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
        titleSub: {
            fontSize: 12,
            color: theme.palette.text.secondary,
            marginTop: 2,
            '& strong': {
                color: theme.palette.text.primary,
                fontWeight: 700,
            },
        },
        closeButton: {
            color: theme.palette.text.secondary,
            padding: 6,
            flexShrink: 0,
        },
        dialogContent: {
            padding: theme.spacing(2, 2.5),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(1.5),
        },
        sectionLabel: {
            fontSize: 11,
            fontWeight: 700,
            color: theme.palette.text.secondary,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            marginBottom: theme.spacing(1),
        },
        fieldsCard: {
            backgroundColor: theme.palette.grey[50],
            borderRadius: 10,
            border: `1px solid ${theme.palette.divider}`,
            padding: theme.spacing(1.5),
        },
        sectionHint: {
            fontSize: 12,
            color: theme.palette.text.secondary,
            marginTop: -8,
            marginBottom: theme.spacing(1),
        },
        editorWrapper: {
            borderRadius: 8,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            '& .tox-tinymce': {
                border: 'none !important',
            },
        },
        // the recipient is the field that matters most: accent border, tint, bold text and label
        toField: {
            '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--rv-accent-blue-bg)',
                fontWeight: 600,
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--rv-accent-blue)',
                borderWidth: 1.5,
            },
            '& .MuiInputLabel-root': {
                color: 'var(--rv-accent-blue)',
                fontWeight: 700,
            },
        },
        textField: {
            backgroundColor: '#fff',
            width: '100%',
        },
        // same dense sizing the Enquiry form gives its user selectors (mui-downshift has no size prop)
        userSelectorDense: {
            backgroundColor: '#fff',
            '& .MuiOutlinedInput-input': {
                padding: '10.5px 14px',
            },
            '& .MuiInputAdornment-positionEnd .MuiIconButton-root': {
                padding: 3,
            },
            '& .MuiInputAdornment-positionEnd .MuiSvgIcon-root': {
                fontSize: 18,
            },
        },
        dialogActions: {
            padding: theme.spacing(1.5, 2.5),
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: theme.spacing(1),
            flexShrink: 0,
        },
        cancelButton: {
            fontSize: 13,
            textTransform: 'none',
            color: theme.palette.text.secondary,
        },
    }),
);

interface DialogHeaderProps {
    title: string;
    subtitle?: React.ReactNode;
    onClose: () => void;
    titleId?: string;
}

export const DialogHeader: React.FC<DialogHeaderProps> = ({ title, subtitle, onClose, titleId = 'form-dialog-title' }) => {
    const classes = useDialogPartsStyles();
    return (
        <div className={classes.dialogHeader}>
            <div>
                <Typography className={classes.titleText} id={titleId}>{title}</Typography>
                {!!subtitle && <Typography className={classes.titleSub} component="div">{subtitle}</Typography>}
            </div>
            <IconButton className={classes.closeButton} size="small" onClick={onClose} aria-label="Close">
                <CloseIcon fontSize="small" />
            </IconButton>
        </div>
    );
};

interface DialogLoadingSkeletonProps {
    title: string;
    onClose: () => void;
    // field rows / columns of the placeholder (match the real form so nothing jumps when it loads)
    rows?: number;
    columns?: number;
}

// Shown inside the dialog while its data loads: the real title and close button, with the fields and buttons as skeletons.
export const DialogLoadingSkeleton: React.FC<DialogLoadingSkeletonProps> = ({ title, onClose, rows = 2, columns = 2 }) => {
    const classes = useDialogPartsStyles();
    return (
        <>
            <DialogHeader title={title} onClose={onClose} />
            <DialogContent className={classes.dialogContent} dividers={false}>
                <SkeletonLoader variant="form" rows={rows} columns={columns} />
            </DialogContent>
            <DialogActions className={classes.dialogActions} disableSpacing={true}>
                <SkeletonLoader variant="shape" shape="text" width={90} height={34} />
                <SkeletonLoader variant="shape" shape="text" width={70} height={34} />
            </DialogActions>
        </>
    );
};
